const AuthConstants = require('./constants');
const Storage = require('utils/storage');
const ClientErrorReporting = require('services/client-error-reporting');

const internals = {};

internals.sensitiveKeys = new Set([
    'accesstoken',
    'authorization',
    'clientsecret',
    'code',
    'cookie',
    'idtoken',
    'mfatoken',
    'nearpeer',
    'newpassword',
    'newpasswordconfirm',
    'password',
    'refreshtoken',
    'token',
    'twilio',
    'verificationtoken'
]);

internals.safeGetSsoRuntimeInspection = () => {

    if (typeof internals.getSsoRuntimeInspection !== 'function') {
        return null;
    }

    try {
        return internals.getSsoRuntimeInspection();
    }
    catch (err) {
        return {
            event: 'sso_runtime_inspection_failed',
            error: err?.message || String(err)
        };
    }
};


module.exports = {
    isDebugBuildEnabled,
    isDeveloperConsoleEnabled,
    isTeamConsoleEnabled,
    isDebugPanelEnabled,
    isDebugEnabled,
    enableDebug,
    disableDebug,
    installDeveloperConsole,
    getDeveloperSnapshot,
    observeDeveloperSnapshot,
    getBundleInspection,
    log,
    warn,
    error,
    recordFlow,
    getStoredFlows,
    clearStoredFlows,
    installRecentErrorCapture,
    recordRecentError,
    getStoredRecentErrors,
    clearStoredRecentErrors,
    captureApiCall,
    recordApiExchange,
    getStoredApiExchanges,
    clearStoredApiExchanges
};

function isDebugBuildEnabled() {

    return process.env.AUTH_MANAGEMENT_DEBUG === true
        || process.env.AUTH_MANAGEMENT_DEBUG === 'true'
        || isDebugPanelEnabled();
}

function isDeveloperConsoleEnabled() {

    if (process.env.NEARPEER_DEV_CONSOLE_ENABLED === true
    || process.env.NEARPEER_DEV_CONSOLE_ENABLED === 'true'
    ) {
        return true;
    }

    if (typeof window === 'undefined') {
        return false;
    }

    const runtimeConfig = window.__NEARPEER_RUNTIME_CONFIG__ || {};

    return runtimeConfig.NEARPEER_DEV_CONSOLE_ENABLED === true;
}

function isTeamConsoleEnabled() {

    return false;
}

function isDebugPanelEnabled() {

    return isDeveloperConsoleEnabled();
}

function isDebugEnabled() {

    if (!isDebugBuildEnabled()) {
        return false;
    }

    if (isDebugPanelEnabled()) {
        return true;
    }

    if (typeof window === 'undefined') {
        return process.env.DEBUG?.includes('auth-management') || process.env.DEBUG?.includes('app:*');
    }

    return Storage.getItem(AuthConstants.debugStorageKey) === 'true'
        || process.env.DEBUG?.includes('auth-management')
        || process.env.DEBUG?.includes('app:*');
}

function enableDebug() {

    if (typeof window === 'undefined') {
        return;
    }

    Storage.setItem(AuthConstants.debugStorageKey, 'true');
}

function disableDebug() {

    if (typeof window === 'undefined') {
        return;
    }

    Storage.removeItem(AuthConstants.debugStorageKey);
}

function installDeveloperConsole({ redux, selectors, store } = {}) {

    if (typeof window === 'undefined') {
        return;
    }

    installRecentErrorCapture();

    const authManagement = {
        isDebugBuildEnabled,
        isDebugPanelEnabled,
        isDebugEnabled,
        enableDebug,
        disableDebug,
        getStoredFlows,
        clearStoredFlows,
        recordRecentError,
        getStoredRecentErrors,
        clearStoredRecentErrors,
        recordApiExchange,
        getStoredApiExchanges,
        clearStoredApiExchanges,
        inspect: () => getDeveloperSnapshot({ redux, selectors, store }),
        inspectServices: () => internals.getServiceInspection({ redux, selectors, store }),
        inspectSso: () => internals.getSsoInspection({ redux, selectors, store }),
        inspectBundle: () => getBundleInspection(),
        getSnapshot: () => getDeveloperSnapshot({ redux, selectors, store })
    };

    window.nearpeer = {
        ...(window.nearpeer || {}),
        console: {
            ...(window.nearpeer?.console || {}),
            inspect: authManagement.inspect,
            inspectServices: authManagement.inspectServices,
            inspectSso: authManagement.inspectSso,
            inspectBundle: authManagement.inspectBundle
        },
        developerConsole: {
            ...(window.nearpeer?.developerConsole || {}),
            inspect: authManagement.inspect,
            inspectServices: authManagement.inspectServices,
            inspectSso: authManagement.inspectSso,
            inspectBundle: authManagement.inspectBundle,
            authManagement
        }
    };

    // Convenience alias for quick mobile/webview console access.
    window.authManagementDebug = authManagement;
    window.nearpeerInspect = authManagement.inspect;
}

function getDeveloperSnapshot(context = {}) {

    const {
        redux,
        selectors,
        store
    } = internals.resolveDebugContext(context);

    const services = internals.getServiceInspection({
        redux,
        selectors,
        store
    });

    return internals.sanitize({
        enabled: isDebugEnabled(),
        panelEnabled: isDebugPanelEnabled(),
        developerConsoleEnabled: isDeveloperConsoleEnabled(),
        teamConsoleEnabled: isTeamConsoleEnabled(),
        debugStorageKey: AuthConstants.debugStorageKey,
        flowStorageKey: AuthConstants.flowStorageKey,
        bundle: getBundleInspection(),
        location: typeof window === 'undefined' ? undefined : {
            pathname: window.location?.pathname,
            search: window.location?.search
        },
        services,
        // Top-level aliases used by the console panels. These are derived live
        // from Redux selectors via services above.
        auth: services.auth,
        sso: services.sso,
        ssoRuntime: internals.safeGetSsoRuntimeInspection(),
        communication: services.communication,
        signup: services.signup,
        routeDependency: internals.getRouteDependencyInspection(),
        persisted: internals.readPersistedContexts(),
        apiExchanges: getStoredApiExchanges(),
        recentErrors: getStoredRecentErrors(),
        flows: getStoredFlows()
    });
}


function getBundleInspection() {

    if (typeof window === 'undefined') {
        return {
            platform: 'server',
            resources: [],
            totals: {}
        };
    }

    const resources = internals.getBundleResourceEntries();

    return internals.sanitize({
        platform: window.cordova?.platformId || 'web',
        at: new Date().toISOString(),
        route: {
            pathname: window.location?.pathname,
            search: window.location?.search
        },
        totals: internals.summarizeBundleResources(resources),
        resources
    });
}


function observeDeveloperSnapshot(context = {}, onSnapshot, options = {}) {

    if (typeof window === 'undefined' || typeof onSnapshot !== 'function') {
        return () => undefined;
    }

    const intervalMs = options.intervalMs || 1000;
    const resolvedContext = internals.resolveDebugContext(context);
    const unsubscribers = [];
    let disposed = false;
    let lastFingerprint = null;

    const emit = (trigger) => {

        if (disposed) {
            return;
        }

        const snapshot = getDeveloperSnapshot(context);
        const observed = internals.getObservedSnapshotState(snapshot);
        const fingerprint = JSON.stringify(observed);

        if (trigger !== 'poll' || fingerprint !== lastFingerprint) {
            lastFingerprint = fingerprint;

            onSnapshot({
                snapshot,
                observer: {
                    at: new Date().toISOString(),
                    trigger,
                    observed
                }
            });
        }
    };

    const subscribe = resolvedContext.store?.subscribe
        || resolvedContext.redux?.subscribe
        || resolvedContext.redux?.store?.subscribe
        || resolvedContext.redux?.hooks?.subscribe;

    if (typeof subscribe === 'function') {
        unsubscribers.push(subscribe(() => emit('redux')));
    }

    const addWindowListener = (eventName, trigger) => {

        const listener = () => emit(trigger);
        window.addEventListener(eventName, listener);

        unsubscribers.push(() => window.removeEventListener(eventName, listener));
    };

    addWindowListener('storage', 'storage');
    addWindowListener('nearpeer:auth-management-debug:update', 'auth-debug');
    addWindowListener('nearpeer:client-debug:update', 'client-debug');
    addWindowListener('popstate', 'route');
    addWindowListener('hashchange', 'route');
    addWindowListener('focus', 'focus');

    const visibilityListener = () => {

        if (!document.hidden) {
            emit('visibility');
        }
    };

    document.addEventListener('visibilitychange', visibilityListener);
    unsubscribers.push(() => document.removeEventListener('visibilitychange', visibilityListener));

    const interval = window.setInterval(() => emit('poll'), intervalMs);
    unsubscribers.push(() => window.clearInterval(interval));

    emit('initial');

    return () => {

        disposed = true;
        unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
}

function log(event, details) {

    if (!isDebugEnabled()) {
        return;
    }

    console.info(AuthConstants.logTag, event, internals.sanitize(details));
}

function warn(event, details) {

    if (!isDebugBuildEnabled()) {
        return;
    }

    console.warn(AuthConstants.logTag, event, internals.sanitize(details));
}

function error(event, details) {

    console.error(AuthConstants.logTag, event, internals.sanitize(details));
}


function recordApiExchange({
    name,
    request,
    result,
    error,
    meta
} = {}) {

    const entry = {
        at: new Date().toISOString(),
        name,
        request: internals.sanitize(request),
        result: internals.sanitize(result),
        error: internals.serializeApiError(error),
        meta: internals.sanitize(meta)
    };

    if (error) {
        recordRecentError({
            source: 'api',
            message: `${name || 'api'} failed`,
            error,
            details: {
                name,
                request,
                result,
                meta
            }
        });
    }

    if (!isDebugBuildEnabled() || typeof window === 'undefined') {
        return entry;
    }

    try {
        const exchanges = getStoredApiExchanges();
        const nextExchanges = [entry, ...exchanges].slice(0, AuthConstants.maxStoredApiExchanges || 20);
        Storage.setItem(AuthConstants.apiDebugStorageKey, JSON.stringify(nextExchanges));
    }
    catch (err) {
        warn('api_exchange_store_failed', {
            message: err?.message
        });
    }

    return entry;
}

async function captureApiCall({ func, name, request } = {}) {

    try {
        const result = await func();

        recordApiExchange({
            name,
            request,
            result
        });

        return result;
    }
    catch (error) {
        recordApiExchange({
            name,
            request,
            error
        });

        throw error;
    }
}

function getStoredApiExchanges() {

    if (!isDebugBuildEnabled() || typeof window === 'undefined') {
        return [];
    }

    try {
        const value = Storage.getItem(AuthConstants.apiDebugStorageKey);
        const parsed = value ? JSON.parse(value) : [];

        return Array.isArray(parsed) ? parsed : [];
    }
    catch (err) {
        warn('api_exchange_read_failed', {
            message: err?.message
        });
        return [];
    }
}

function clearStoredApiExchanges() {

    if (typeof window === 'undefined') {
        return;
    }

    Storage.removeItem(AuthConstants.apiDebugStorageKey);
}


function installRecentErrorCapture() {

    if (typeof window === 'undefined') {
        return;
    }

    if (!window.__nearpeerRecentErrorCaptureInstalled) {
        window.__nearpeerRecentErrorCaptureInstalled = true;

        window.addEventListener('error', (event) => {

            const details = {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno
            };

            recordRecentError({
                source: 'window.error',
                message: event.message,
                error: event.error,
                details
            });

            ClientErrorReporting.reportClientError({
                error: event.error || event.message,
                source: 'window.error',
                type: 'uncaught_error',
                details
            }).catch(() => {});
        });

        window.addEventListener('unhandledrejection', (event) => {

            recordRecentError({
                source: 'window.unhandledrejection',
                message: event.reason?.message || String(event.reason || 'Unhandled promise rejection'),
                error: event.reason
            });

            ClientErrorReporting.reportClientError({
                error: event.reason || 'Unhandled promise rejection',
                source: 'window.unhandledrejection',
                type: 'unhandled_rejection'
            }).catch(() => {});
        });
    }

    internals.installNetworkErrorCapture();
}

function recordRecentError({
    source = 'app',
    message,
    error,
    details
} = {}) {

    const serializedError = internals.serializeRecentError(error);
    const entry = internals.sanitize({
        at: new Date().toISOString(),
        source,
        message: message || serializedError?.message || 'Unknown error',
        status: serializedError?.status,
        code: serializedError?.code,
        url: serializedError?.url,
        method: serializedError?.method,
        data: serializedError?.data,
        stack: serializedError?.stack,
        details
    });

    error?.message && warn('recent_error_recorded', {
        source,
        message: entry.message,
        status: entry.status,
        url: entry.url
    });

    if (!isDebugBuildEnabled() || typeof window === 'undefined') {
        return entry;
    }

    try {
        const errors = getStoredRecentErrors();
        const nextErrors = [entry, ...errors].slice(0, AuthConstants.maxStoredErrors || 50);
        Storage.setItem(AuthConstants.errorStorageKey, JSON.stringify(nextErrors));
        window.dispatchEvent(new Event('nearpeer:auth-management-debug:update'));
    }
    catch (err) {
        warn('recent_error_store_failed', {
            message: err?.message
        });
    }

    return entry;
}

function getStoredRecentErrors() {

    if (!isDebugBuildEnabled() || typeof window === 'undefined') {
        return [];
    }

    try {
        const value = Storage.getItem(AuthConstants.errorStorageKey);
        const parsed = value ? JSON.parse(value) : [];

        return Array.isArray(parsed) ? parsed : [];
    }
    catch (err) {
        warn('recent_error_read_failed', {
            message: err?.message
        });
        return [];
    }
}

function clearStoredRecentErrors() {

    if (typeof window === 'undefined') {
        return;
    }

    Storage.removeItem(AuthConstants.errorStorageKey);
    window.dispatchEvent(new Event('nearpeer:auth-management-debug:update'));
}

function recordFlow(event, details = {}) {

    const entry = {
        at: new Date().toISOString(),
        event,
        details: internals.sanitize(details)
    };

    log(event, entry.details);

    if (!isDebugBuildEnabled() || typeof window === 'undefined') {
        return entry;
    }

    try {
        const flows = getStoredFlows();
        const nextFlows = [entry, ...flows].slice(0, AuthConstants.maxStoredFlows);
        Storage.setItem(AuthConstants.flowStorageKey, JSON.stringify(nextFlows));
    }
    catch (err) {
        warn('flow_store_failed', {
            message: err?.message
        });
    }

    return entry;
}

function getStoredFlows() {

    if (!isDebugBuildEnabled() || typeof window === 'undefined') {
        return [];
    }

    try {
        const value = Storage.getItem(AuthConstants.flowStorageKey);
        const parsed = value ? JSON.parse(value) : [];

        return Array.isArray(parsed) ? parsed : [];
    }
    catch (err) {
        warn('flow_read_failed', {
            message: err?.message
        });
        return [];
    }
}

function clearStoredFlows() {

    if (typeof window === 'undefined') {
        return;
    }

    Storage.removeItem(AuthConstants.flowStorageKey);
}


internals.resolveDebugContext = (context = {}) => {

    return {
        redux: context.redux?.hooks || context.redux || {},
        selectors: context.selectors?.all || context.selectors || {},
        store: context.store || context.redux?.store || context.redux?.hooks?.store || null
    };
};

internals.getState = ({ redux, store } = {}) => (
    store?.getState?.()
    || redux?.getState?.()
    || redux?.hooks?.getState?.()
    || redux?.store?.getState?.()
    || {}
);

internals.getServiceInspection = (context = {}) => {

    const {
        redux,
        selectors,
        store
    } = internals.resolveDebugContext(context);
    const state = internals.getState({ redux, store });

    return internals.sanitize({
        auth: internals.readSafely(() => internals.readAuthInspection({
            selectors,
            state
        })),
        sso: internals.getSsoInspection({ redux, selectors, store }),
        communication: internals.readSafely(() => internals.readCommunicationInspection({
            selectors,
            state
        })),
        signup: internals.readSafely(() => ({
            context: selectors?.getSignupContext?.(state),
            authenticate: selectors?.getSignupAuthenticateDetails?.(state)
        })),
        persisted: internals.readPersistedContexts()
    });
};

internals.getSsoInspection = (context = {}) => {

    const {
        redux,
        selectors,
        store
    } = internals.resolveDebugContext(context);
    const state = internals.getState({ redux, store });

    return internals.readSafely(() => {
        const Sso = require('./sso');
        return Sso.getSsoInspection({
            loginContext: selectors?.getLoginContext?.(state),
            signupContext: selectors?.getSignupContext?.(state),
            currentSchool: selectors?.getCurrentSchool?.(state),
            persisted: internals.readPersistedContexts()
        });
    });
};


internals.readAuthInspection = ({ selectors, state }) => {

    const tokens = internals.getAuthTokensFromState(state);
    const selectorAuthenticated = selectors?.getIsAuthenticated?.(state);
    const tokenAuthenticated = !!tokens.nearpeer;

    return {
        isAuthenticated: selectorAuthenticated || tokenAuthenticated,
        status: selectors?.getAuthStatus?.(state),
        isVerified: selectors?.getVerifiedStatus?.(state),
        loginContext: selectors?.getLoginContext?.(state),
        tokenStatus: {
            nearpeer: tokens.nearpeer ? 'present' : 'missing',
            twilio: tokens.twilio ? 'present' : 'missing',
            source: tokens.source
        }
    };
};

internals.readCommunicationInspection = ({ selectors, state }) => {

    let twilioToken = null;

    try {
        twilioToken = selectors?.getTwilioToken?.(state);
    }
    catch (err) {
        twilioToken = null;
    }

    const tokens = internals.getAuthTokensFromState(state);
    const hasTwilioToken = !!twilioToken || !!tokens.twilio;
    const runtime = typeof window === 'undefined' ? null : window.__nearpeerCommunicationDebug || null;
    const recovery = typeof window === 'undefined' ? null : window.__nearpeerTwilioRecoveryDebug || null;
    const route = internals.getCommunicationRouteInspection(state);

    return {
        twilioTokenPresent: hasTwilioToken,
        twilioTokenStatus: hasTwilioToken ? 'present' : 'missing',
        recovery,
        route,
        listening: runtime ? {
            event: runtime.event,
            at: runtime.at,
            currentChannelSid: runtime.currentChannelSid,
            previousChannelSid: runtime.previousChannelSid,
            requestedChannelSid: runtime.requestedChannelSid,
            normalizedChannelSid: runtime.normalizedChannelSid,
            isValidChannelSid: runtime.isValidChannelSid,
            listeningStarted: runtime.listeningStarted,
            error: runtime.error
        } : null,
        mismatch: {
            routeVsListening: !!(route.channelSid && runtime?.currentChannelSid && route.channelSid !== runtime.currentChannelSid)
        }
    };
};

internals.getCommunicationRouteInspection = (state = {}) => {

    if (typeof window === 'undefined') {
        return {};
    }

    const pathname = window.location?.pathname || '';
    const classChatMatch = pathname.match(/^\/app\/classes\/chat\/([^/?#]+)/);
    const dmChatMatch = pathname.match(/^\/app\/messaging\/([^/?#]+)/);
    const announcementMatch = pathname.match(/^\/app\/classes\/announcement\/chat\/([^/?#]+)/);
    const channelSid = classChatMatch?.[1] || dmChatMatch?.[1] || null;
    const channels = state.dataFetching?.entities?.channels || {};
    const channelKeys = Object.keys(channels);

    return {
        pathname,
        kind: classChatMatch ? 'class-chat'
            : dmChatMatch ? 'dm-chat'
                : announcementMatch ? 'announcement-chat'
                    : pathname.includes('/app/classes') ? 'classes'
                        : pathname.includes('/app/messaging') ? 'messaging'
                            : 'other',
        channelSid,
        announcementClassId: announcementMatch?.[1] || null,
        channelSidLooksValid: !channelSid || (
            typeof channelSid === 'string'
            && channelSid.trim()
            && channelSid !== 'NaN'
            && channelSid !== 'undefined'
            && channelSid !== 'null'
        ),
        channelInReduxEntities: channelSid ? !!channels[channelSid] : false,
        channelEntityCount: channelKeys.length,
        channelEntitySample: channelKeys.slice(0, 10)
    };
};

internals.getAuthTokensFromState = (state = {}) => {

    const candidates = [
        { source: 'state.auth.tokens', value: state.auth?.tokens },
        { source: 'state.auth', value: state.auth },
        { source: 'state.authentication.tokens', value: state.authentication?.tokens },
        { source: 'state.session.tokens', value: state.session?.tokens }
    ];

    const selected = candidates.find(({ value }) => value?.nearpeer || value?.twilio);

    return {
        nearpeer: selected?.value?.nearpeer || null,
        twilio: selected?.value?.twilio || null,
        source: selected?.source || null
    };
};


internals.getObservedSnapshotState = (snapshot = {}) => ({
    location: snapshot.location,
    auth: {
        isAuthenticated: snapshot.services?.auth?.isAuthenticated,
        status: snapshot.services?.auth?.status,
        isVerified: snapshot.services?.auth?.isVerified,
        loginContext: snapshot.services?.auth?.loginContext,
        tokenStatus: snapshot.services?.auth?.tokenStatus
    },
    sso: {
        sso: snapshot.services?.sso?.sso,
        provider: snapshot.services?.sso?.provider,
        promptLogin: snapshot.services?.sso?.promptLogin,
        runtimeEvent: snapshot.ssoRuntime?.event
    },
    communication: snapshot.services?.communication,
    routeDependency: snapshot.routeDependency,
    signup: {
        context: snapshot.services?.signup?.context
    },
    recentErrors: snapshot.recentErrors?.length || 0,
    apiExchanges: snapshot.apiExchanges?.length || 0,
    flows: snapshot.flows?.length || 0
});

internals.readSafely = (reader) => {

    try {
        return reader();
    }
    catch (err) {
        return {
            error: err?.message || String(err)
        };
    }
};

internals.getRouteDependencyInspection = () => {

    if (typeof window === 'undefined') {
        return null;
    }

    return window.__nearpeerRouteDependencyDebug || null;
};

internals.readPersistedContexts = () => {

    if (typeof window === 'undefined') {
        return {};
    }

    return {
        loginContext: internals.parseStoredJson(AuthConstants.loginContext.storageKey),
        signupContext: internals.parseStoredJson(AuthConstants.signupContext.storageKey),
        ssoCallbackContext: internals.parseStoredJson(AuthConstants.persistence.keys.sso.callbackContext)
    };
};

internals.parseStoredJson = (key) => {

    try {
        const value = Storage.getItem(key);

        return value ? JSON.parse(value) : null;
    }
    catch (err) {
        return {
            error: err?.message || String(err)
        };
    }
};



internals.installNetworkErrorCapture = () => {

    if (typeof window === 'undefined' || window.__nearpeerNetworkErrorCaptureInstalled) {
        return;
    }

    window.__nearpeerNetworkErrorCaptureInstalled = true;

    internals.installFetchErrorCapture();
    internals.installXhrErrorCapture();
};

internals.installFetchErrorCapture = () => {

    if (typeof window.fetch !== 'function' || window.fetch.__nearpeerWrapped) {
        return;
    }

    const originalFetch = window.fetch;

    const wrappedFetch = async (...args) => {

        const startedAt = Date.now();
        const request = internals.getFetchRequestDetails(args);

        try {
            const response = await originalFetch.apply(window, args);

            if (!response.ok && internals.shouldCaptureNetworkError(request.url)) {
                response.clone().text().then((body) => {

                    internals.recordNetworkError({
                        source: 'fetch',
                        request,
                        status: response.status,
                        statusText: response.statusText,
                        body,
                        durationMs: Date.now() - startedAt
                    });
                }).catch(() => {

                    internals.recordNetworkError({
                        source: 'fetch',
                        request,
                        status: response.status,
                        statusText: response.statusText,
                        body: null,
                        durationMs: Date.now() - startedAt
                    });
                });
            }

            return response;
        }
        catch (err) {
            if (internals.shouldCaptureNetworkError(request.url)) {
                recordRecentError({
                    source: 'fetch',
                    message: `${request.method} ${request.url} failed`,
                    error: err,
                    details: {
                        request,
                        durationMs: Date.now() - startedAt
                    }
                });
            }

            throw err;
        }
    };

    wrappedFetch.__nearpeerWrapped = true;
    window.fetch = wrappedFetch;
};

internals.installXhrErrorCapture = () => {

    const XHR = window.XMLHttpRequest;

    if (!XHR?.prototype?.open || XHR.prototype.open.__nearpeerWrapped) {
        return;
    }

    const originalOpen = XHR.prototype.open;
    const originalSend = XHR.prototype.send;

    XHR.prototype.open = function nearpeerDebugOpen(method, url, ...rest) {

        this.__nearpeerDebugRequest = {
            method: (method || 'GET').toUpperCase(),
            url: internals.normalizeNetworkUrl(url)
        };

        return originalOpen.call(this, method, url, ...rest);
    };

    XHR.prototype.open.__nearpeerWrapped = true;

    XHR.prototype.send = function nearpeerDebugSend(...args) {

        const startedAt = Date.now();

        this.addEventListener('loadend', () => {

            const request = this.__nearpeerDebugRequest || {
                method: 'GET',
                url: ''
            };

            if (this.status < 400 || !internals.shouldCaptureNetworkError(request.url)) {
                return;
            }

            internals.recordNetworkError({
                source: 'xhr',
                request,
                status: this.status,
                statusText: this.statusText,
                body: internals.getXhrResponseText(this),
                durationMs: Date.now() - startedAt
            });
        });

        this.addEventListener('error', () => {

            const request = this.__nearpeerDebugRequest || {
                method: 'GET',
                url: ''
            };

            if (!internals.shouldCaptureNetworkError(request.url)) {
                return;
            }

            recordRecentError({
                source: 'xhr',
                message: `${request.method} ${request.url} network error`,
                error: {
                    message: 'XHR network error',
                    config: request
                },
                details: {
                    request,
                    durationMs: Date.now() - startedAt
                }
            });
        });

        return originalSend.apply(this, args);
    };
};

internals.getFetchRequestDetails = (args) => {

    const input = args[0];
    const init = args[1] || {};

    return {
        method: (init.method || input?.method || 'GET').toUpperCase(),
        url: internals.normalizeNetworkUrl(input?.url || input)
    };
};

internals.normalizeNetworkUrl = (url) => {

    if (!url) {
        return '';
    }

    try {
        return new URL(String(url), window.location.origin).href;
    }
    catch (err) {
        return String(url);
    }
};

internals.shouldCaptureNetworkError = (url) => {

    if (!url) {
        return false;
    }

    const value = String(url);

    return value.includes('/api/')
        || value.includes('/protocol/openid-connect/')
        || value.includes('/oauth/token')
        || value.includes('/authorize')
        || value.includes('sso.nearpeer.net')
        || value.includes('sso.staging.nearpeer.net')
        || value.includes('login.nearpeer.net')
        || value.includes('auth0.com')
        || value.includes('localhost:4000')
        || value.includes(window.location.origin);
};

internals.getXhrResponseText = (xhr) => {

    try {
        if (!xhr.responseType || xhr.responseType === 'text') {
            return xhr.responseText;
        }

        return `[${xhr.responseType} response]`;
    }
    catch (err) {
        return null;
    }
};

internals.recordNetworkError = ({
    source,
    request,
    status,
    statusText,
    body,
    durationMs
}) => {

    const data = internals.parseNetworkBody(body);
    const message = `${request.method} ${request.url} failed with ${status}`;

    recordRecentError({
        source,
        message,
        error: {
            message,
            response: {
                status,
                statusText,
                data
            },
            config: {
                method: request.method,
                url: request.url
            }
        },
        details: {
            durationMs,
            responseBody: internals.truncateNetworkBody(body)
        }
    });
};

internals.parseNetworkBody = (body) => {

    if (typeof body !== 'string') {
        return body || null;
    }

    try {
        return JSON.parse(body);
    }
    catch (err) {
        return internals.truncateNetworkBody(body);
    }
};

internals.truncateNetworkBody = (body) => {

    if (typeof body !== 'string') {
        return body || null;
    }

    return body.length > 4000 ? `${body.slice(0, 4000)}...` : body;
};

internals.serializeApiError = (error) => {

    if (!error) {
        return undefined;
    }

    return internals.sanitize({
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method
    });
};


internals.serializeRecentError = (error) => {

    if (!error) {
        return undefined;
    }

    return internals.sanitize({
        message: error.message,
        code: error.code || error.response?.data?.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        stack: error.stack
    });
};

internals.sanitize = (details) => {

    if (!details || typeof details !== 'object') {
        return details;
    }

    return JSON.parse(JSON.stringify(details, (key, value) => {

        const normalizedKey = key.toLowerCase().replaceAll('_', '').replaceAll('-', '');

        if (key === '__raw' || internals.sensitiveKeys.has(normalizedKey)) {
            return value ? '[redacted]' : value;
        }

        return value;
    }));
};


internals.getBundleResourceEntries = () => {

    if (typeof window === 'undefined' || !window.performance?.getEntriesByType) {
        return [];
    }

    return window.performance.getEntriesByType('resource')
        .filter((entry) => internals.isBundleResource(entry.name))
        .map((entry) => ({
            name: internals.toRelativeResourceName(entry.name),
            initiatorType: entry.initiatorType,
            durationMs: Math.round(entry.duration || 0),
            transferSize: entry.transferSize || 0,
            encodedBodySize: entry.encodedBodySize || 0,
            decodedBodySize: entry.decodedBodySize || 0,
            cached: entry.transferSize === 0 && entry.encodedBodySize > 0
        }))
        .sort((a, b) => b.encodedBodySize - a.encodedBodySize);
};

internals.isBundleResource = (name = '') => {

    try {
        const url = new URL(name, window.location.origin);
        return url.origin === window.location.origin
            && /^\/(?:js|css|assets|fonts)\//.test(url.pathname)
            && /\.(?:js|css|png|jpe?g|svg|ico|woff2?|eot|ttf|otf)$/.test(url.pathname);
    }
    catch (err) {
        return false;
    }
};

internals.toRelativeResourceName = (name = '') => {

    try {
        const url = new URL(name, window.location.origin);
        return `${url.pathname}${url.search || ''}`;
    }
    catch (err) {
        return String(name);
    }
};

internals.summarizeBundleResources = (resources = []) => resources.reduce((collector, resource) => ({
    resources: collector.resources + 1,
    transferSize: collector.transferSize + (resource.transferSize || 0),
    encodedBodySize: collector.encodedBodySize + (resource.encodedBodySize || 0),
    decodedBodySize: collector.decodedBodySize + (resource.decodedBodySize || 0),
    cachedResources: collector.cachedResources + (resource.cached ? 1 : 0),
    jsResources: collector.jsResources + (/\.js(?:\?|$)/.test(resource.name) ? 1 : 0),
    cssResources: collector.cssResources + (/\.css(?:\?|$)/.test(resource.name) ? 1 : 0)
}), {
    resources: 0,
    transferSize: 0,
    encodedBodySize: 0,
    decodedBodySize: 0,
    cachedResources: 0,
    jsResources: 0,
    cssResources: 0
});
